import React, { useEffect, useState, useRef } from "react";

import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Stack from "@mui/material/Stack";
import { styled, useTheme } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

type PasswordTextFieldProps = {
  autoFocus?: boolean;
  value: string;
  label: string;
  onChange: (password: string) => void;
  onEnter: () => void;
  onTab?: () => void;
  inputRef?: any;
  error?: any;
};

function PasswordTextField({
  autoFocus,
  value,
  label,
  onChange,
  onEnter,
  onTab,
  inputRef,
  error,
}: PasswordTextFieldProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState(value);
  const handleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const handleBlur = () => {};

  const handleChange = (e: React.ChangeEvent<HTMLElement>) => {
    const value = (e.target as any).value;
    setPassword(value);
    onChange(value);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLElement>) => {
    if (onEnter) {
      if (e.key === "Enter") {
        e.preventDefault();
        onEnter();
      } else if (e.key === "Tab" && onTab) {
        e.preventDefault();
        onTab();
      }
    }
  };

  return (
    <TextField
      style={{ width: "180px" }}
      variant="standard"
      label={label}
      value={password}
      size="small"
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKey}
      autoFocus={autoFocus}
      inputRef={inputRef}
      type="text"
      error={error}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleShowPassword}
              onMouseDown={handleMouseDownPassword}
              edge="end"
              size="small"
              sx={{
                padding: '4px',
                color: 'var(--text-tertiary)',
                '&:hover': {
                  color: 'var(--text-secondary)',
                },
              }}
            >
              {showPassword ? (
                <VisibilityOff sx={{ fontSize: 16 }} />
              ) : (
                <Visibility sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </InputAdornment>
        ),
      }}
      sx={{
        '& .MuiInput-root': {
          fontSize: '14px',
        },
        '& .MuiInputLabel-root': {
          fontSize: '12px',
          color: 'var(--text-secondary)',
        },
        '& .MuiInput-root::before': {
          borderBottomColor: 'rgba(255, 255, 255, 0.12)',
        },
        '& .MuiInput-root:hover::before': {
          borderBottomColor: 'rgba(255, 255, 255, 0.2)',
        },
        '& .MuiInput-root::after': {
          borderBottomColor: 'var(--accent-primary)',
        },
        input: {
          color: 'var(--text-primary)',
          fontSize: '14px',
          ...(showPassword
            ? {}
            : {
                textSecurity: 'disc',
                MozTextSecurity: 'disc',
                WebkitTextSecurity: 'disc',
              }),
        },
      }}
    />
  );
}

export default function useClickOutside(): [
  React.MutableRefObject<undefined | HTMLElement>,
  boolean
] {
  const [clicked, setClicked] = useState(false);
  const ref = useRef<HTMLElement>();

  const handleClick = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setClicked(true);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClick, true);
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  return [ref, clicked];
}

type PasswordInputProps = {
  password?: string;
  onPassword: (password: string) => void;
  onCancel: () => void;
  confirm: boolean;
  label: string;
  error?: any;
};

export function PasswordInput({
  password,
  onPassword,
  onCancel,
  confirm,
  label,
  error,
}: PasswordInputProps) {
  const theme = useTheme();
  const [password1, setPassword1] = useState<string>(password || "");
  const [password2, setPassword2] = useState<string>(password || "");
  const [ref, clickedOutside] = useClickOutside();
  const inputRef = useRef<HTMLElement>();

  const handlePassword1 = (value: string) => {
    setPassword1(value);
  };

  const handlePassword2 = (value: string) => {
    setPassword2(value);
  };

  const handleOk = () => {
    if (canDelete()) {
      onPassword("");
    } else if (password1 && (!confirm || password1 === password2)) {
      onPassword(password1);
    }
  };

  const canDelete = () => {
    return password && password === password1 && password === password2;
  };

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.keyCode === 27) {
        onCancel();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, []);

  useEffect(() => {
    if (clickedOutside) {
      onCancel();
    }
  }, [clickedOutside]);

  const handleEnter1 = () => {
    if (!confirm && password1) {
      onPassword(password1);
    }

    if (confirm && inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleEnter2 = () => {
    if (!canDelete() && password1 && password1 === password2) {
      onPassword(password1);
    }
  };

  return (
    <Box
      ref={ref}
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? '#1f1f1f' : '#ffffff',
        color: 'var(--text-primary)',
        borderRadius: '12px',
        border: theme.palette.mode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.1)'
          : '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 20px rgba(0, 0, 0, 0.5)'
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        p: 3,
        display: "inline-block",
        minWidth: "240px",
      }}
    >
      <Stack direction="column" gap={2} alignItems={"stretch"}>
        <Typography
          sx={{
            fontSize: '12px',
            color: 'var(--text-secondary)',
            textAlign: 'center',
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        <PasswordTextField
          error={error}
          value={password1}
          label={"Password"}
          onChange={handlePassword1}
          onEnter={handleEnter1}
          onTab={handleEnter1}
          autoFocus
        />
        {confirm && (
          <PasswordTextField
            error={error}
            value={password2}
            label={"Confirm"}
            onChange={handlePassword2}
            onEnter={handleEnter2}
            inputRef={inputRef}
          />
        )}
        <Stack direction={"row"} sx={{ mt: 1 }} gap={1} justifyContent="flex-end">
          <Button
            size="small"
            onClick={onCancel}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: 'var(--text-secondary)',
              minWidth: 'auto',
              px: 2,
              py: 0.75,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            disabled={!(password1 && (!confirm || password1 === password2))}
            onClick={handleOk}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 500,
              backgroundColor: 'var(--accent-primary)',
              color: '#ffffff',
              minWidth: 'auto',
              px: 2,
              py: 0.75,
              borderRadius: '8px',
              '&:hover:not(:disabled)': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? '#6fa0ff'
                  : '#1565c0',
              },
              '&:disabled': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(0, 0, 0, 0.05)',
                color: 'var(--text-tertiary)',
                cursor: 'not-allowed',
              },
            }}
          >
            {canDelete() ? "Delete" : "OK"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export const PopupButton = styled(Button)(({ theme }) => ({
  color: theme.palette.getContrastText(grey[500]),
  backgroundColor: grey[700],
  "&:hover": {
    backgroundColor: grey[500],
  },
  fontSize: 8,
}));
